// src/pages/ResetPassword.js
import { LockOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  message,
} from "antd";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import React, { useState } from "react";
import { passwordReset } from "@directus/sdk";
import client from "../helpers/directusClient";

const { Title, Text } = Typography;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onFinish = async (values) => {
    if (!token) return;
    setLoading(true);
    try {
      await client.request(passwordReset(token, values.password));
      setDone(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (error) {
      console.error("Password reset error:", error);
      message.error(
        "Could not reset password. The link may have expired — request a new one."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row
      justify="center"
      align="middle"
      style={{
        height: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "0 15px",
      }}
    >
      <Col xs={24} sm={20} md={16} lg={12} xl={8}>
        <Card
          hoverable
          style={{
            borderRadius: 12,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: "40px 32px" }}
        >
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <img
              src="/images/logo.png"
              alt="KwachaPlus"
              style={{
                height: 96,
                maxWidth: "100%",
                marginBottom: 24,
                borderRadius: 8,
              }}
            />
            <Title level={2} style={{ marginBottom: 8, color: "#2D3748" }}>
              Reset Password
            </Title>
            <Text type="secondary" style={{ fontSize: 14 }}>
              Choose a new password for your account.
            </Text>
          </div>

          {!token ? (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Alert
                message="Missing reset token"
                description="This page must be opened from the link in the reset email."
                type="error"
                showIcon
              />
              <div style={{ textAlign: "center" }}>
                <Link
                  to="/forgot-password"
                  style={{ color: "#667eea", fontWeight: 500 }}
                >
                  Request a new reset link
                </Link>
              </div>
            </Space>
          ) : done ? (
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Alert
                message="Password updated"
                description="Your password has been reset. Redirecting you to sign in..."
                type="success"
                showIcon
              />
              <div style={{ textAlign: "center" }}>
                <Link
                  to="/login"
                  style={{ color: "#667eea", fontWeight: 500 }}
                >
                  <ArrowLeftOutlined /> Go to sign in
                </Link>
              </div>
            </Space>
          ) : (
            <Form
              name="resetPasswordForm"
              layout="vertical"
              autoComplete="off"
              onFinish={onFinish}
            >
              <Form.Item
                label="New Password"
                name="password"
                rules={[
                  { required: true, message: "Please enter a new password." },
                  { min: 8, message: "Password must be at least 8 characters." },
                ]}
                hasFeedback
                style={{ marginBottom: 24 }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="Enter new password"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <Form.Item
                label="Confirm Password"
                name="confirm"
                dependencies={["password"]}
                hasFeedback
                rules={[
                  { required: true, message: "Please confirm your password." },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("The two passwords do not match.")
                      );
                    },
                  }),
                ]}
                style={{ marginBottom: 24 }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="Re-enter new password"
                  size="large"
                  style={{ borderRadius: 8 }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  style={{
                    height: 48,
                    borderRadius: 8,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    border: "none",
                    fontWeight: 600,
                  }}
                >
                  Update Password
                </Button>
              </Form.Item>

              <div style={{ textAlign: "center" }}>
                <Link
                  to="/login"
                  style={{ color: "#667eea", fontSize: 14, fontWeight: 500 }}
                >
                  <ArrowLeftOutlined /> Back to sign in
                </Link>
              </div>
            </Form>
          )}
        </Card>
      </Col>
    </Row>
  );
};

export default ResetPassword;
